import React, { useState, useEffect, Fragment } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import { CircularProgress } from '@material-ui/core';
import { useBoardsStyles } from './BoardsStyles';
// Define interfaces locally instead of importing from mocks
interface BoardPurchase {
  id: string;
  isPrimary: boolean;
  canceled: boolean;
  poNumber: string;
  mudCount: number;
  boardPurchaseFloors: any[];
}

interface BoardType {
  id: string;
  name: string;
  shortName: string;
  thickness: number;
  width: number;
  length: number;
}

interface BoardReceiptFloor {
  id: string;
  floor: number;
  quantity: number;
  boardTypeId: string;
  boardPurchaseId: string;
}
import { PermissionsProvider } from 'providers/permissionsProvider';
import { CAN_SIGN_OFF, CAN_CHECK_COUNT } from 'providers/permissions';
import CheckCountDialog from './Boards/CheckCountDialog';

interface FloorListRow {
  row: number;
  boards: {
    [boardTypeId: string]: {
      boardTypeId: string;
      floor: number;
      quantity: number;
    };
  };
}

interface FloorListItem {
  floor: number;
  rows: FloorListRow[];
}

interface BoardTableProps {
  boardPurchase: BoardPurchase;
  signReport: (floors: FloorListItem[], boardPurchase: BoardPurchase) => void;
  boardReceiptFloors: BoardReceiptFloor[];
  theJob: any;
  loadingBoards?: boolean;
}

export const BoardTable: React.FC<BoardTableProps> = ({
  boardPurchase,
  signReport,
  boardReceiptFloors,
  theJob,
  loadingBoards = false,
}) => {
  const classes = useBoardsStyles();
  const { hasPermission } = PermissionsProvider.useContainer();
  const [floors, setFloors] = useState<FloorListItem[]>([]);
  const [boardTypes, setBoardTypes] = useState<BoardType[]>([]);
  const [loadingBoardTypes, setLoadingBoardTypes] = useState(true);
  const [openCheckCount, setOpenCheckCount] = useState(false);
  const [loadingSignOff, setLoadingSignOff] = useState(false);

  // Initialize floors and board types from boardPurchase
  useEffect(() => {
    if (boardPurchase) {
      // If there are receipt floors (after signoff), use only board types from receipts
      if (boardReceiptFloors.length > 0) {
        // Get unique board types from receipt floors only
        const uniqueBoardTypeIds = [...new Set(
          boardReceiptFloors.map(brf => brf.boardTypeId)
        )];

        // Get board types from theJob.boardTypes
        const boardTypesForPurchase = theJob.boardTypes.filter(bt => 
          uniqueBoardTypeIds.includes(bt.id)
        ).sort((a, b) => a.sortPriority - b.sortPriority);

        setBoardTypes(boardTypesForPurchase);
        setFloors([]); // Empty floors since floorsMap will handle this
      } else {
        // Before signoff - use original purchase order data
        // Get unique floors
        const uniqueFloors = [...new Set(
          boardPurchase.boardPurchaseFloors.map(bpf => bpf.floor)
        )].sort();

        // Get unique board types
        const uniqueBoardTypeIds = [...new Set(
          boardPurchase.boardPurchaseFloors.map(bpf => bpf.boardTypeId)
        )];

        // Get board types from theJob.boardTypes
        const boardTypesForPurchase = theJob.boardTypes.filter(bt => 
          uniqueBoardTypeIds.includes(bt.id)
        ).sort((a, b) => a.sortPriority - b.sortPriority);

        setBoardTypes(boardTypesForPurchase);

        // Initialize floors structure with empty boards (like original)
        const floorsStructure: FloorListItem[] = uniqueFloors.map(floor => ({
          floor,
          rows: [{ row: 1, boards: {} }], // Start with empty boards for user to fill
        }));

        setFloors(floorsStructure);
      }
      
      setLoadingBoardTypes(false);
    }
  }, [boardPurchase, theJob.boardTypes, boardReceiptFloors]);

  const addFloorRow = (floor: number) => () => {
    const newFloors = [...floors];
    const floorFound = newFloors.find(floorItem => floorItem.floor === floor);
    if (floorFound) {
      floorFound.rows.push({ row: floorFound.rows.length + 1, boards: {} });
      setFloors(newFloors);
    }
  };

  const removeFloorRow = (floor: number, rowIndex: number) => () => {
    const newFloors = [...floors];
    const floorFound = newFloors.find(floorItem => floorItem.floor === floor);
    if (floorFound && floorFound.rows.length > 1) {
      floorFound.rows.splice(rowIndex, 1);
      // Renumber rows
      floorFound.rows.forEach((row, index) => {
        row.row = index + 1;
      });
      setFloors(newFloors);
    }
  };

  const getBoardQuantity = (row: FloorListRow, boardType: BoardType, _floor: number) => {
    const quantity = row.boards[boardType.id]?.quantity;
    return quantity || quantity === 0 ? quantity : 0;
  };

  const setBoardQuantity = (floorItem: FloorListItem, row: FloorListRow, boardType: BoardType) => (event: any) => {
    const value = event.target.value;
    const quantity = value === '' ? 0 : parseInt(value) || 0;
    const newFloors = [...floors];
    const targetFloor = newFloors.find(f => f.floor === floorItem.floor);
    if (targetFloor) {
      const targetRow = targetFloor.rows.find(r => r.row === row.row);
      if (targetRow) {
        if (value === '' || quantity === 0) {
          // Remove the board entry when empty or 0
          delete targetRow.boards[boardType.id];
        } else {
          targetRow.boards[boardType.id] = {
            boardTypeId: boardType.id,
            floor: floorItem.floor,
            quantity,
          };
        }
        setFloors(newFloors);
      }
    }
  };


  const getTotalBoards = (currentFloorsMap?: any) => {
    const floorsToUse = currentFloorsMap || floorsMap;
    return floorsToUse.reduce((total: number, floor: any) => {
      return total + floor.rows.reduce((rowTotal: number, row: any) => {
        return rowTotal + Object.values(row.boards).reduce((boardTotal: number, board: any) => {
          return boardTotal + board.quantity;
        }, 0);
      }, 0);
    }, 0);
  };

  const isSameBoardsQuantity = () => {
    if (boardReceiptFloors.length) {
      let sumPurchase = 0;
      boardPurchase.boardPurchaseFloors.map((floor: any) => {
        sumPurchase += floor.quantity;
      });
      let sumReceipt = 0;
      boardReceiptFloors.map((boardReceipt: any) => {
        sumReceipt += boardReceipt.quantity;
      });
      return sumPurchase === sumReceipt;
    }
    if (boardPurchase.boardPurchaseFloors) {
      const sumFilled: number = getTotalBoards();
      let sumPurchase = 0;
      boardPurchase.boardPurchaseFloors.map((floor: any) => {
        sumPurchase += floor.quantity;
      });
      return sumFilled === sumPurchase;
    } else {
      return true;
    }
  };

  const getMudCount = () => {
    const mud = (boardPurchase as any).materials?.find(
      (material: any) => material.name.toLowerCase() === 'mud',
    );
    if (mud) {
      return mud.amount;
    }
    return boardPurchase.mudCount || 0;
  };

  const handleSignReport = async () => {
    try {
      setLoadingSignOff(true);
      await signReport(floors, boardPurchase);
    } finally {
      setLoadingSignOff(false);
    }
  };

  const sum = (items: any[], prop: string) => {
    return items.reduce((a, b) => a + b[prop], 0);
  };

  // Calculate sum for a specific board type column (similar to v1)
  const sumColumn = (boardType: any) => {
    let count = 0;
    floorsMap.forEach((floor: any) => {
      floor.rows.forEach((row: any) => {
        if (row.boards[boardType.id] && row.boards[boardType.id].quantity) {
          count += parseInt(row.boards[boardType.id].quantity.toString());
        }
      });
    });
    return count;
  };

  const isCountedWrong = (boardType: BoardType) => {
    if (!boardReceiptFloors.length) {
      return false;
    }
    const originalPO = sum(
      boardPurchase.boardPurchaseFloors.filter(
        (boardReceipt: any) => boardReceipt.boardTypeId === boardType.id,
      ),
      'quantity',
    );
    const counted = sum(
      boardReceiptFloors.filter(
        (boardReceipt: any) => boardReceipt.boardTypeId === boardType.id,
      ),
      'quantity',
    );
    return originalPO !== counted;
  };

  if (loadingBoardTypes || loadingBoards) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  // Use floorsMap logic like original - show receipt floors if they exist
  let floorsMap = floors;
  if (boardReceiptFloors.length) {
    const floorsReceipt: any = [];
    boardReceiptFloors
      .filter((item: any) => item.quantity > 0)
      .forEach(item => {
        const floorReceiptFound: any = floorsReceipt.find(
          (floor: any) => floor.floor === item.floor,
        );
        if (floorReceiptFound) {
          const alreadyExist = floorReceiptFound.rows.filter(
            row => row.boards[item.boardTypeId],
          ).length;
          if (alreadyExist > 0) {
            floorReceiptFound.rows.push({
              row: alreadyExist + 1,
              boards: {
                [item.boardTypeId]: {
                  boardTypeId: item.boardTypeId,
                  quantity: item.quantity,
                  floor: item.floor,
                },
              },
            });
          } else {
            floorReceiptFound.rows[0].boards[item.boardTypeId] = {
              boardTypeId: item.boardTypeId,
              quantity: item.quantity,
              floor: item.floor,
            };
          }
        } else {
          const newFloorReceipt = {
            floor: item.floor,
            rows: [
              {
                row: 1,
                boards: {
                  [item.boardTypeId]: {
                    boardTypeId: item.boardTypeId,
                    quantity: item.quantity,
                    floor: item.floor,
                  },
                },
              },
            ],
          };
          floorsReceipt.push(newFloorReceipt);
        }
      });
    floorsMap = floorsReceipt;
  }

  return (
    <Box>
      {/* Header with buttons and total */}
      <Box display="flex" alignItems="center" justifyContent="flex-end">
        {boardReceiptFloors.length && hasPermission(CAN_CHECK_COUNT) ? (
          <Button
            color="primary"
            variant="outlined"
            disabled={!boardPurchase.isPrimary}
            size="large"
            className={classes.button}
            onClick={() => setOpenCheckCount(true)}
          >
            Check Count
          </Button>
        ) : (
          ''
        )}
        {boardReceiptFloors.length || !hasPermission(CAN_SIGN_OFF) ? (
          ''
        ) : (
          <Button
            color="primary"
            variant="outlined"
            disabled={!boardPurchase.isPrimary || loadingSignOff}
            size="large"
            className={classes.button}
            onClick={handleSignReport}
          >
            {loadingSignOff ? (
              <CircularProgress size={20} />
            ) : (
              'Sign Off'
            )}
          </Button>
        )}
        <Typography
          className={
            isSameBoardsQuantity() ? classes.totalBoardsGreen : classes.totalBoards
          }
        >
          {getTotalBoards()}
        </Typography>
      </Box>

      {/* Table Header */}
      <Box display="flex" width="100%">
        <Box display="flex" width="20%" minWidth="20%" justifyContent="center">
          <Typography>Floor</Typography>
        </Box>
        {boardTypes.length > 0 &&
          boardTypes.map((boardType: BoardType) => (
            <Box
              key={boardType.id}
              display="flex"
              width={`${79 / boardTypes.length + 1}%`}
              maxWidth="150px"
              justifyContent="center"
              className={isCountedWrong(boardType) ? classes.contedWrong : ''}
            >
              <Typography>{boardType.shortName}</Typography>
            </Box>
          ))}
        <Box
          display="flex"
          width={`${79 / boardTypes.length + 1}%`}
          maxWidth="150px"
          justifyContent="center"
        >
          <Typography>Mud</Typography>
        </Box>
      </Box>

      {/* Table Body */}
      <Box display="flex" width="100%" flexDirection="column">
        {floorsMap.length > 0 &&
          floorsMap.map((floor: FloorListItem, floorIndex: number) => (
            <Box key={floor.floor} className={classes.floorGroup}>
              {floor.rows.map((row: FloorListRow, rowIndex: number) => (
                <Box
                  display="flex"
                  width="100%"
                  key={`${floor.floor}-${row.row}`}
                  className={classes.floorRow}
                >
                  <Box
                    display="flex"
                    width="20%"
                    minWidth="20%"
                    justifyContent="center"
                    alignItems="center"
                  >
                    {rowIndex === 0 ? (
                      <Fragment>
                        <Typography>Floor #{floor.floor}</Typography>
                        {boardReceiptFloors.length ||
                        !hasPermission(CAN_SIGN_OFF) ||
                        !boardPurchase.isPrimary ? (
                          ''
                        ) : (
                          <AddCircleIcon
                            onClick={addFloorRow(floor.floor)}
                            className={classes.pointer}
                          />
                        )}
                      </Fragment>
                    ) : (
                      <Fragment>
                        {boardReceiptFloors.length ||
                        !hasPermission(CAN_SIGN_OFF) ||
                        !boardPurchase.isPrimary ? (
                          ''
                        ) : (
                          <RemoveCircleIcon
                            className={classes.pointer}
                            onClick={removeFloorRow(floor.floor, rowIndex)}
                          />
                        )}
                      </Fragment>
                    )}
                  </Box>
                  {boardTypes.map((boardType: BoardType) => (
                    <Box
                      key={boardType.id}
                      display="flex"
                      width={`${79 / boardTypes.length}%`}
                      maxWidth="150px"
                      justifyContent="center"
                    >
                      <TextField
                        type="number"
                        className={classes.inputGrid}
                        placeholder="0"
                        InputProps={{ inputProps: { min: 0 } }}
                        value={getBoardQuantity(row, boardType, floor.floor)}
                        onChange={setBoardQuantity(floor, row, boardType)}
                        disabled={
                          !hasPermission(CAN_SIGN_OFF) ||
                          (boardReceiptFloors && boardReceiptFloors.length > 0) ||
                          !boardPurchase.isPrimary
                        }
                      />
                    </Box>
                  ))}
                  {floorIndex === 0 && rowIndex === 0 && (
                    <Box
                      display="flex"
                      width={`${79 / boardTypes.length + 1}%`}
                      maxWidth="150px"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Typography>{getMudCount()}</Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          ))}
      </Box>

      {/* Total row - similar to v1 */}
      <Box display="flex" width="100%" pt={2} borderTop="1px solid black">
        <Box
          display="flex"
          width="20%"
          minWidth="20%"
          justifyContent="center"
          alignItems="center"
        >
          <Typography>Total</Typography>
        </Box>
        {boardTypes.length &&
          boardTypes.map((boardType: any) => (
            <Box
              key={boardType.id}
              display="flex"
              width={`${79 / boardTypes.length}%`}
              maxWidth="150px"
              justifyContent={floorsMap.length > 0 ? 'flex-end' : 'center'}
            >
              <TextField
                disabled
                className={classes.inputGrid}
                value={sumColumn(boardType)}
              />
            </Box>
          ))}
      </Box>

      {/* CheckCount Modal */}
      {openCheckCount && (
        <CheckCountDialog
          boardPurchase={boardPurchase}
          boardPurchaseFloors={boardPurchase.boardPurchaseFloors}
          boardReceiptFloors={boardReceiptFloors}
          open={openCheckCount}
          handleClose={() => setOpenCheckCount(false)}
          disabled={!boardPurchase.isPrimary}
        />
      )}

    </Box>
  );
};

export default BoardTable;